import { CheckCircle2, ChevronDown, ChevronUp, Download, File, Video, Youtube, YoutubeIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';

// Components
import { collection, getDocs, getFirestore, query, where } from 'firebase/firestore';
import { app } from "../../firebase"; // Ensure your firebase app is correctly initialized  



interface ModuleListProps {
    modules: any[];
    selectedModuleId: string;
    expandedModules: Record<string, boolean>;
    onModuleSelect: (moduleId: string) => void;
    onToggleModule: (moduleId: string) => void;
    onDownload: (material: any) => void;
    onWatchVideo: (video: any) => void;
    batchIds: any;
}

export const ModuleList: React.FC<ModuleListProps> = ({
    modules,
    selectedModuleId,
    expandedModules,
    onModuleSelect,
    onToggleModule,
    onDownload,
    onWatchVideo,
    batchIds
}) => {
    const db = getFirestore(app);
    const [selectedBatches, setSelectedBatches] = useState([]);
    const [selectedModuleIDNo, setSelectedModuleId] = useState(selectedModuleId);
    const [modulesData, setModules] = useState(modules ?? []);
    useEffect(() => {
        /**
         * Fetches the course batches that correspond to the given batch ids.
         * Sets the state of the component to the fetched batches.
         * If no batchIds are given, nothing is fetched.
         */
        const fetchBatches = async () => {
            if (batchIds?.length > 0) {
                const batchesCollection = collection(db, "courseBatches");
                const batchesQuery = query(batchesCollection, where('id', 'in', batchIds.map(batch => batch)));
                const querySnapshot = await getDocs(batchesQuery);
                const batchesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setSelectedBatches(batchesData);
            }
        };

        fetchBatches();
        setModules(eliminateDuplicates(modules));
    }, [batchIds, modules]);


    const eliminateDuplicates = (input) => {
        let unique = [];
        input.forEach(x => {
            if (unique.filter(m => m.id == x.id)?.length<=0) unique.push(x);
        });
        return unique;
    }

    if (!modules || modules?.length === 0) {
        return (
            <div className="bg-white rounded-xl p-8 border border-gray-200 text-center">
                <p className="text-gray-500">No modules available for this subject.</p>
            </div>
        );
    }

    const getBatchName = (batchId) => {
        const batch = selectedBatches.find((batch: any) => batch.id === batchId);
        return batch ? batch.batchName : '';
    }
    const checkIsBatchExists = (batchId: string) => {
        return batchIds.some((batch: any) => batch.batchId === batchId);
    }

    function checkBatchStatus(batches, batchIds) {
        if(batches==undefined || batchIds==undefined) return false;
        // Check if there are any batchIds and if any batch in batches exists and is completed  
        const hasBatchIds = batchIds?.length > 0; // Check if there are any batchIds  
        const isAnyCompleted = batches?.some(batch =>
            batchIds.includes(batch?.batchId) && batch.isCompleted
        );
        return hasBatchIds && isAnyCompleted;
    }

    const checkIsRecordingEnable = (batchIds, batchId) => {
        return batchIds.some(e => e == batchId);
    }
    return (
        <div className="space-y-8">
            {/* Module pills/tabs */}
            {/* <div className="flex flex-wrap gap-2 mb-4 overflow-x-auto pb-2">
                {modules.map((module) => (
                    <Badge
                        key={module.id}
                        variant={module.id === selectedModuleId ? "default" : "outline"}
                        className={`cursor-pointer px-4 py-2 rounded-full ${module.id === selectedModuleId
                            ? 'bg-primary hover:bg-primary/90'
                            : 'hover:bg-gray-100'
                            }`}
                        onClick={() => onModuleSelect(module.id)}
                    >
                        {module?.moduleName}
                    </Badge>
                ))}
            </div> */}

            {/* Course Modules */}
            <div className="space-y-6">
                {modulesData.map((module, index) => (
                    <div
                        key={module.id + index}
                        // key={module.id}
                        // id={module.id}
                        className={`bg-white rounded-xl border border-gray-200 overflow-hidden ${module.id === selectedModuleIDNo ? 'ring-2 ring-primary ring-opacity-50' : ''
                            }`}
                    >
                        {/* Module Header */}
                        <div
                            className="p-6 flex justify-between items-center cursor-pointer"
                            onClick={() => { onToggleModule(module.id); setSelectedModuleId(module.id) }}
                        >
                            <div className="flex items-center gap-3">
                                {checkBatchStatus(module?.batches, batchIds) ? (
                                    <CheckCircle2 size={20} className="text-green-500" />
                                ) : (
                                    <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                                )}
                                <div>
                                    <h3 className="font-semibold text-lg">{module?.moduleName}</h3>
                                    <p className="text-gray-600 text-sm">{module?.moduleDescription}</p>
                                </div>
                            </div>
                            <div>
                                {expandedModules[module.id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                            </div>
                        </div>

                        {/* Module Content */}
                        {expandedModules[module.id] && (
                            <div className="border-t border-gray-200">
                                {/* Topics */}
                                {module?.moduleTopics && module?.moduleTopics?.length > 0 && (
                                    <div className="p-6 border-b border-gray-100">
                                        <h4 className="font-medium mb-3 text-gray-700">TOPICS</h4>
                                        <ul className="space-y-1"> {/* Changed space-y-2 to space-y-1 for less gap */}
                                            {module?.moduleTopics?.map((moduleItem: any, index: number) => {
                                                const topicKey = Object.keys(moduleItem.topic)[0]; // Get the topic key  
                                                const topics = moduleItem.topic[topicKey]; // Retrieve topics using the key  

                                                return (
                                                    <li key={index} className="flex flex-col items-start text-sm"> {/* Ensure flex-col for consistent alignment */}
                                                        <span className="text-primary">•</span>
                                                        <span className="ml-4 font-semibold">{topicKey}</span> {/* Display topic title */}

                                                        {topics.map((topic: any, topicIndex: number) => (
                                                            <span key={topicIndex} className="ml-8 block"> {/* Increased left margin for subtopics */}
                                                                {topic}
                                                            </span>
                                                        ))}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}

                                {module?.moduleTopics && module?.moduleTopics?.topic?.length > 0 && (
                                    <div className="p-6 border-b border-gray-100">
                                        <h4 className="font-medium mb-3 text-gray-700">TOPICS</h4>
                                        <ul className="space-y-1"> {/* Use space-y-1 for less gap */}
                                            {module?.moduleTopics?.topic?.map((topic: any, index: number) => (
                                                <li key={index} className="flex items-start text-sm"> {/* Maintain items-start for alignment */}
                                                    <span className="text-primary">•</span>
                                                    <span className="ml-2"> {/* Consistent margin left */}
                                                        {topic}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Materials */}
                                {module?.materials && module?.materials?.length > 0 && (
                                    <div className="p-6 border-b border-gray-100">
                                        <h4 className="font-medium mb-3 text-gray-700">MATERIALS</h4>
                                        <ul className="space-y-3">
                                            {module?.materials?.map((material: any) => (
                                                <li key={material?.id || `material-${Math.random()}`} className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <File size={18} className="text-primary" />
                                                        <button
                                                            className="text-primary hover:underline cursor-pointer"
                                                            onClick={() => onDownload(material?.link)}
                                                        >
                                                            {material?.link?.split('/').pop()}
                                                        </button>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {/* <span className="text-xs text-gray-500">{material?.size || '2 MB'}</span> */}
                                                        <Button
                                                            size="icon"
                                                            variant="ghost"
                                                            className="h-8 w-8 hover:bg-[#0071bd]"
                                                            onClick={() => onDownload(material?.link)}
                                                        >
                                                            <Download size={14} />
                                                        </Button>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Videos */}
                                {module?.recordings && module?.recordings?.length > 0 && (
                                    <div className="p-6">
                                        <h4 className="font-medium mb-3 text-gray-700">VIDEO RECORDINGS</h4>
                                        <ul className="space-y-4">
                                            {module.recordings.map((video: any, i: number) => (
                                                checkBatchStatus(module.batches, batchIds) && checkIsRecordingEnable(batchIds, video?.batchId) && (
                                                    <li key={video.id || `video-${Math.random()}`} className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className="relative">
                                                                <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                                                                    <Video size={16} className="text-primary" />
                                                                </div>
                                                                {checkBatchStatus(module.batches, batchIds) && (
                                                                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                                                                        <CheckCircle2 size={10} className="text-white" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div>
                                                                <p className="font-medium">{video?.title ?? module?.moduleName + " " + Number(i + 1)}</p>
                                                                <p className="text-xs text-gray-500">{video?.duration || '30 to 60 mins'}</p>
                                                                {/* Batch Name Badge */}
                                                                <span className="inline-flex items-center px-2 py-1 mt-2 text-xs font-medium text-white bg-[#0071bd] rounded-full">
                                                                    {getBatchName(video?.batchId) || 'Unknown Batch'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="text-primary bg-blue-200 hover:text-primary hover:bg-[#0071bd] hover:text-white flex items-center"
                                                            onClick={() => window.open(video?.video, '_blank')}
                                                        >
                                                            <YoutubeIcon size={20} className="mr-1" /> {/* YouTube icon with margin to the right */}
                                                            {video?.completed ? 'Watch Again' : 'Watch'} {/* Conditional button text */}
                                                        </Button>
                                                    </li>
                                                )
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};
